import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Document, Page, Text, View, Image, StyleSheet, PDFViewer, pdf } from "@react-pdf/renderer";
import LoadTemplate from "./LoadTemplate";
import { useDropzone } from "react-dropzone";
import api, { API_BASE_URL } from "../../src/utils/api";

const API_UPLOAD = `/upload-logo`;
const API_SEND_EMAIL = `/send-email`;

const styles = StyleSheet.create({
  page: { flexDirection: "column", padding: 50, fontSize: 12 },
  headerContainer: { flexDirection: "row", justifyContent: "space-between", marginBottom: 20 },
  logo: { width: 80, height: 80 },
  companyInfo: { textAlign: "right" },
  content: { marginTop: 20, fontSize: 12 },
  footer: { position: "absolute", bottom: 20, left: 40, right: 40, fontSize: 10, textAlign: "center" },
});



const availableTags = [
  "{RecipientName}", "{RecipientAddress}", "{ViolationType}",
  "{ViolationID}", "{ViolationDate}", "{CureByDate}", "{body}",
  "{Sender}", "{SenderTitle}", "{CompanyName}", "{CompanyAddress}","{CurrentDate}",
];

const NotificationLetter = ({ formData, tags }) => {
  const formattedLayout = replaceTags(formData.layout, tags);

  return (
    <Document>
      <Page size="LETTER" style={styles.page}>
        <View style={styles.headerContainer}>
          {formData.logo && <Image src={formData.logo} style={styles.logo} />}
          <View style={styles.companyInfo}>
            <Text>{formData.companyName}</Text>
            <Text>{formData.companyAddress}</Text>
          </View>
        </View>
        <View style={styles.content}>
          <Text>{formattedLayout}</Text>
        </View>
        <View style={styles.footer}>
          <Text>{formData.footer}</Text>
        </View>
      </Page>
    </Document>
  );
};

const replaceTags = (template, tags) => {
  if (!template) return "";

  return Object.entries(tags).reduce((result, [key, value]) => {
    const placeholder = `{${key}}`;

    // Trim spaces and replace multiple spaces with a single space
    const cleanedValue = value ? value.replace(/\s+/g, ' ').trim() : '';

    return result.replaceAll(placeholder, cleanedValue);
  }, template)
  .replaceAll("{CurrentDate}", new Date().toLocaleDateString()); // Replace {CurrentDate} with today's date
};



const CoverLetters = ({ tags, violation_id }) => {
  const [formData, setFormData] = useState({
    layout: "Dear {RecipientName},\n\nYour violation details:\n{body}\n\nSincerely,\n{Sender}",
    companyName: "Company Name",
    companyAddress: "123 Main St, City, State",
    footer: "This is an official notification.",
    sender: "John Doe",
    senderTitle: "Property Manager",
    logo: null,
    violation_id: violation_id || "",
  });

  useEffect(() => {
    if (violation_id) {
      setFormData((prevState) => ({
        ...prevState,
        violation_id,
      }));
    }
  }, [violation_id]);

  const [selectedTag, setSelectedTag] = useState("");
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState("");

  const textareaRef = useRef(null);

  

  const handleInsertTag = () => {
    if (!selectedTag || !textareaRef.current) return;
    const textarea = textareaRef.current;
    const cursorPosition = textarea.selectionStart;
    const textBefore = formData.layout.substring(0, cursorPosition);
    const textAfter = formData.layout.substring(cursorPosition);
    setFormData({ ...formData, layout: textBefore + selectedTag + textAfter });

    setTimeout(() => textarea.focus(), 0);
  };

  const handleSendEmail = () => setShowEmailModal(true);

  const closeModal = () => {
    setShowEmailModal(false);
    setRecipientEmail("");
  };
  
  const confirmSendEmail = async () => {
    if (!recipientEmail) return alert("Please enter a recipient email.");
    if (!formData.violation_id) return alert("Violation ID is missing!");
  
    const pdfBlob = await pdf(<NotificationLetter formData={formData} tags={tags} />).toBlob();
  
    const emailData = new FormData();
    emailData.append("pdf", pdfBlob, "notification_letter.pdf");
    emailData.append("recipient", recipientEmail);
    emailData.append("violation_id", formData.violation_id);
  
    try {
      await api.post(API_SEND_EMAIL, emailData, { headers: { "Content-Type": "multipart/form-data" } });
      alert("Email sent successfully!");
      closeModal();
    } catch (error) {
      console.error("Error sending email:", error);
      alert("Failed to send email");
    }
  };

  const styles = `
  .container {
    max-width: 800px;
    margin: 0 auto;
    padding: 20px;
  }

  .page-title {
    font-size: 24px;
    font-weight: bold;
    margin-bottom: 20px;
  }

  .template-section {
    text-align: center;
    margin-bottom: 20px;
  }

  .form-group {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 15px;
  }

  .form-input {
    flex: 1;
    padding: 8px;
  }

  .text-area-container {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  textarea {
    width: 100%;
    min-height: 120px;
    padding: 8px;
    border: 1px solid #ccc;
    border-radius: 5px;
    resize: vertical;
  }

  .button-container {
    display: flex;
    justify-content: flex-start;
    gap: 10px;
  }

  .custom-button {
    padding: 10px 15px;
    border: none;
    border-radius: 5px;
    cursor: pointer;
    font-size: 14px;
    transition: background 0.3s ease;
  }

  .custom-button-primary {
    background-color: #007bff;
    color: white;
  }

  .custom-button-success {
    background-color: #007bff;
    color: white;
  }

  .custom-button:hover {
    opacity: 0.9;
  }
`;

return (
  <div className="container">
    <style>{styles}</style>

    <h2 className="page-title">Notification Letters</h2>

    <div className="template-section">
      <LoadTemplate onTemplateLoad={(template) => {
        setFormData((prevState) => ({ ...prevState, ...template }));
      }} />
    </div>

    <div className="form-group">
      <label>Select a Tag to Insert:</label>
      <select className="form-input" value={selectedTag} onChange={(e) => setSelectedTag(e.target.value)}>
        <option value="">-- Select a Tag --</option>
        {availableTags.map(tag => <option key={tag} value={tag}>{tag}</option>)}
      </select>
      <button className="custom-button custom-button-primary" onClick={handleInsertTag}>
        Insert Tag
      </button>
    </div>

    <div className="text-area-container">
      <textarea ref={textareaRef} value={formData.layout} onChange={(e) => setFormData({ ...formData, layout: e.target.value })}></textarea>

      <div className="button-container">
        <button className="custom-button custom-button-success" onClick={handleSendEmail}>
          Send Email
        </button>
        {/* Email Modal */}
    {showEmailModal && (
      <div className="modal-overlay">
        <div className="modal-content">
          <h3>Enter Recipient Email</h3>
          <input
            type="email"
            className="modal-input"
            value={recipientEmail}
            onChange={(e) => setRecipientEmail(e.target.value)}
            placeholder="Recipient Email"
          />
          <div className="modal-buttons">
            <button className="custom-button custom-button-primary" onClick={confirmSendEmail}>
              Confirm & Send
            </button>
            <button className="custom-button custom-button-danger" onClick={closeModal}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    )}
      </div>
    </div>

    <PDFViewer style={{ width: "100%", height: "85vh", marginTop: "20px" }}>
      <NotificationLetter formData={formData} tags={tags} />
    </PDFViewer>
  </div>
);

};

export default CoverLetters;
